import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./textArea.html";
import textArea from '~/components/c360/textArea/textArea.html';
import textAreaWithToggle from '~/components/c360/textArea/textAreaWithToggle.html';
import { showRemainingCharactersInfo } from '~/modules/utilities/inputUtilities';
export const DEFAULT_NUMBER_OF_ROWS = 8;
const UNLOCALIZED_LABEL = '** MISSING I18N **';
class C360TextArea extends LightningElement {
  constructor(...args) {
    super(...args);
    this.identifier = '';
    this.label = '';
    this.overview = '';
    this.badge = '';
    this.maxLength = Number.MAX_VALUE;
    this.placeholder = '';
    this.value = '';
    this.labelledBy = '';
    this.disabled = false;
    this.required = false;
    this.requiredText = UNLOCALIZED_LABEL;
    this.helpInputMessage = '';
    this.showRemainingCharacters = false;
    this.forceShowError = false;
    this.rows = DEFAULT_NUMBER_OF_ROWS;
    this.hasToggle = false;
    this.enabledText = UNLOCALIZED_LABEL;
    this.disabledText = UNLOCALIZED_LABEL;
    this.alwaysShowRemainingCharacters = false;
    this.changeCallback = void 0;
    this.getCharactersRemainingText = void 0;
    this.changeBlur = void 0;
    this.changeFocus = void 0;
    this.changeCallbackToggle = void 0;
    this.showRemainingCharactersAtPercentage = 10;
    this._hasRendered = false;
    this._hasBlurred = false;
    this._currentCharacters = 0;
    this._toggleIsActive = false;
  }
  get toggleIsActive() {
    return this._toggleIsActive;
  }
  set toggleIsActive(value) {
    this._toggleIsActive = value;
  }
  render() {
    if (this.hasToggle) {
      return textAreaWithToggle;
    }
    return textArea;
  }
  get currentCharacters() {
    return this._currentCharacters;
  }
  set currentCharacters(value) {
    if (value && !isNaN(Number(value))) {
      this._currentCharacters = value;
    } else {
      this._currentCharacters = 0;
    }
  }
  get showHelpInputMessage() {
    return this.helpInputMessage;
  }
  get requiredCss() {
    if (this.hasToggle) {
      return this.toggleIsActive && this.required ? 'required' : '';
    }
    return this.required ? 'required' : '';
  }
  get remainingCharacters() {
    return this.maxLength - this.currentCharacters;
  }
  get showRequiredError() {
    if (this.hasToggle) {
      return this.toggleIsActive && this.required && this.currentCharacters === 0 && (this.forceShowError || this._hasBlurred);
    }
    return this.required && this.currentCharacters === 0 && (this.forceShowError || this._hasBlurred);
  }
  get cssClass() {
    if (this.showRequiredError || this.hasTooManyCharacters) {
      return 'error textarea slds-textarea';
    }
    return 'textarea slds-textarea';
  }
  connectedCallback() {
    if (!this.label && !(this.labelledBy || this.ariaLabelledBy)) {
      console.error('<c360-text-area> was created without label or labelled-by attributes, this is an accessibility issue. All inputs should have a label associated with them.');
    }
    this.currentCharacters = typeof this.value === 'string' ? this.value.length : 0;
  }
  renderedCallback() {
    if (!this._hasRendered) {
      const input = this.template.querySelector('textarea');
      if (input) {
        var _querySelector$getAtt, _querySelector;
        input.value = this.value;
        input.setAttribute('aria-labelledby', this.label ? (_querySelector$getAtt = (_querySelector = this.template.querySelector('label')) === null || _querySelector === void 0 ? void 0 : _querySelector.getAttribute('id')) !== null && _querySelector$getAtt !== void 0 ? _querySelector$getAtt : 'textAreaLabel' : this.labelledBy || this.ariaLabelledBy || '');
        this._hasRendered = true;
      }
    }
  }
  onChanged(event) {
    if (this.changeCallback) {
      this.changeCallback(event.target.value, event);
    }
    this.currentCharacters = event.target.value.length;
  }
  onFocus(event) {
    this._hasBlurred = false;
    event.preventDefault();
    if (this.changeFocus) {
      this.changeFocus(event.target.value, event);
    }
  }
  onBlur(event) {
    this._hasBlurred = true;
    event.preventDefault();
    if (this.changeBlur) {
      this.changeBlur(event.target.value, event);
    }
  }
  get showRemainingCharactersInfo() {
    return this.alwaysShowRemainingCharacters || this.showRemainingCharacters && showRemainingCharactersInfo(this.currentCharacters, this.maxLength, this.showRemainingCharactersAtPercentage);
  }
  get charactersRemainingDescription() {
    if (this.getCharactersRemainingText) {
      return this.getCharactersRemainingText(this.remainingCharacters);
    }
    return `${this.remainingCharacters} characters remaining`;
  }
  get remainingCharactersCSS() {
    return this.hasTooManyCharacters ? 'remaining slds-grid_align-end required-message' : 'remaining slds-grid_align-end';
  }
  get hasTooManyCharacters() {
    return this.maxLength && this.remainingCharacters < 0;
  }
  onChangeToggle() {
    this.changeCallbackToggle();
    this._toggleIsActive = !this._toggleIsActive;
  }
  /*LWC compiler v7.1.3*/
}
C360TextArea.delegatesFocus = true;
_registerDecorators(C360TextArea, {
  publicProps: {
    identifier: {
      config: 0
    },
    label: {
      config: 0
    },
    overview: {
      config: 0
    },
    badge: {
      config: 0
    },
    maxLength: {
      config: 0
    },
    placeholder: {
      config: 0
    },
    value: {
      config: 0
    },
    labelledBy: {
      config: 0
    },
    disabled: {
      config: 0
    },
    required: {
      config: 0
    },
    requiredText: {
      config: 0
    },
    helpInputMessage: {
      config: 0
    },
    showRemainingCharacters: {
      config: 0
    },
    forceShowError: {
      config: 0
    },
    rows: {
      config: 0
    },
    hasToggle: {
      config: 0
    },
    enabledText: {
      config: 0
    },
    disabledText: {
      config: 0
    },
    alwaysShowRemainingCharacters: {
      config: 0
    },
    changeCallback: {
      config: 0
    },
    getCharactersRemainingText: {
      config: 0
    },
    changeBlur: {
      config: 0
    },
    changeFocus: {
      config: 0
    },
    changeCallbackToggle: {
      config: 0
    },
    showRemainingCharactersAtPercentage: {
      config: 0
    },
    toggleIsActive: {
      config: 3
    }
  },
  fields: ["_hasRendered", "_hasBlurred", "_currentCharacters", "_toggleIsActive"]
});
const __lwc_component_class_internal = _registerComponent(C360TextArea, {
  tmpl: _tmpl,
  sel: "c360-text-area",
  apiVersion: 62
});
export default __lwc_component_class_internal;